// Vista PDF de una COTIZACIÓN / PROFORMA: abre una ventana A4 imprimible (el
// usuario la guarda como PDF desde el diálogo y se la manda al cliente).
// Mismo patrón que facturaPdf.ts, pero este documento NO es tributario: lleva
// el rótulo COTIZACIÓN, su validez bien visible y una nota que lo aclara — para
// que nadie lo confunda con una factura.
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const fmt = (v: number, moneda: string) =>
  (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: moneda || 'USD' });
const fecha = (d: unknown) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');

interface LineaCot {
  numero_linea: number; codigo: string | null; descripcion: string;
  cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number;
  subtotal: number; iva_valor: number;
}
interface CotDet {
  numero: string; razon_social: string; identificacion: string;
  email: string | null; telefono: string | null;
  fecha: string; fecha_validez: string; dias_validez: number; estado: string;
  moneda: string; subtotal: number; total_descuento: number; iva: number; importe_total: number;
  condiciones: string | null; observaciones: string | null;
  vencida: boolean; lineas: LineaCot[];
}

export async function abrirCotizacionPdf(id: string): Promise<void> {
  const det = (await apiClient.post('/rpc/cot_obtener', { _id: id }, FIN)).data as CotDet | null;
  if (!det) throw new Error('Cotización no encontrada');

  const me = (await apiClient.post('/rpc/me', {})).data as { user?: { empresa?: { id?: string } } };
  const empId = me?.user?.empresa?.id;
  const emp = empId
    ? ((await apiClient.get(`/rest/autenticacion.empresas?id=eq.${empId}&select=nombre,ruc,direccion,telefono,logo`)).data?.[0] ?? {})
    : {};

  const mon = det.moneda || 'USD';
  const lineas = det.lineas ?? [];
  const filas = lineas.map((l, i) => `
    <tr><td class="c">${i + 1}</td><td class="mono">${esc(l.codigo || '—')}</td><td>${esc(l.descripcion)}</td>
    <td class="r">${Number(l.cantidad).toLocaleString(LOCALE)}</td>
    <td class="r">${fmt(Number(l.precio_unitario), mon)}</td>
    <td class="r">${Number(l.descuento) > 0 ? fmt(Number(l.descuento) * Number(l.cantidad), mon) : '—'}</td>
    <td class="r">${Number(l.iva_tarifa)}%</td>
    <td class="r">${fmt(Number(l.subtotal) + Number(l.iva_valor), mon)}</td></tr>`).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8">
  <title>Cotización ${esc(det.numero)}</title>
  <style>
    @page { size: A4; margin: 16mm; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #222; position: relative; }
    h1 { font-size: 17px; margin: 0 0 2px; } h2 { font-size: 13px; margin: 10px 0 2px; color: #444; }
    .meta { margin: 2px 0; } .mono { font-family: monospace; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th, td { border: 1px solid #999; padding: 4px 6px; } th { background: #f0f0f0; text-align: left; font-size: 11px; }
    .r { text-align: right; } .c { text-align: center; } .tot td { font-weight: bold; }
    .caja { border: 1px solid #999; padding: 8px 10px; margin-top: 10px; }
    .validez { border: 1px solid #2563eb; background: #eff6ff; padding: 8px 10px; margin-top: 10px; color: #1e3a8a; }
    .vencida { border-color: #c33; background: #fef2f2; color: #991b1b; }
    .marca { position: fixed; top: 40%; left: 10%; transform: rotate(-24deg); font-size: 56px; font-weight: 800;
             color: rgba(37, 99, 235, 0.12); letter-spacing: 4px; pointer-events: none; z-index: 99; }
    .logo { max-height: 60px; max-width: 180px; float: right; }
  </style></head><body>
  <div class="marca">COTIZACIÓN</div>
  ${emp.logo ? `<img class="logo" src="${esc(emp.logo)}" alt=""/>` : ''}
  <h1>${esc(emp.nombre ?? '')}</h1>
  ${emp.ruc ? `<div class="meta">RUC: ${esc(emp.ruc)}</div>` : ''}
  ${emp.direccion ? `<div class="meta">${esc(emp.direccion)}</div>` : ''}
  ${emp.telefono ? `<div class="meta">Tel: ${esc(emp.telefono)}</div>` : ''}
  <h2>COTIZACIÓN ${esc(det.numero)}</h2>
  <div class="meta"><b>Fecha:</b> ${fecha(det.fecha)}</div>
  <div class="meta"><b>Cliente:</b> ${esc(det.razon_social)} · ${esc(det.identificacion)}</div>
  ${det.email ? `<div class="meta"><b>Correo:</b> ${esc(det.email)}</div>` : ''}
  ${det.telefono ? `<div class="meta"><b>Teléfono:</b> ${esc(det.telefono)}</div>` : ''}
  <table>
    <thead><tr><th>#</th><th>Código</th><th>Descripción</th><th class="r">Cant.</th><th class="r">P. unit.</th><th class="r">Desc.</th><th class="r">IVA</th><th class="r">Total</th></tr></thead>
    <tbody>${filas}</tbody>
    <tfoot>
      <tr><td colspan="7" class="r">Subtotal</td><td class="r">${fmt(Number(det.subtotal), mon)}</td></tr>
      ${Number(det.total_descuento) > 0 ? `<tr><td colspan="7" class="r">Descuento</td><td class="r">−${fmt(Number(det.total_descuento), mon)}</td></tr>` : ''}
      <tr><td colspan="7" class="r">IVA</td><td class="r">${fmt(Number(det.iva), mon)}</td></tr>
      <tr class="tot"><td colspan="7" class="r">TOTAL</td><td class="r">${fmt(Number(det.importe_total), mon)}</td></tr>
    </tfoot>
  </table>
  <div class="validez${det.vencida ? ' vencida' : ''}">
    <b>${det.vencida ? 'Esta cotización venció el' : 'Válida hasta el'} ${fecha(det.fecha_validez)}</b>
    ${det.vencida ? ' — los precios deben confirmarse antes de pedir.' : ` (${det.dias_validez} día${det.dias_validez === 1 ? '' : 's'} desde su emisión). Pasada esa fecha los precios pueden variar.`}
  </div>
  ${det.condiciones ? `<div class="caja"><b>Condiciones:</b> ${esc(det.condiciones)}</div>` : ''}
  ${det.observaciones ? `<div class="caja"><b>Observaciones:</b> ${esc(det.observaciones)}</div>` : ''}
  <div class="caja" style="border-color:#999;color:#555">
    <b>Documento sin validez tributaria.</b> Esta cotización no es una factura ni sustituye a un
    comprobante de venta: es una oferta comercial. Al aceptarla se emitirá la factura correspondiente.
  </div>
  </body></html>`;

  const win = window.open('', '_blank', 'width=900,height=720');
  if (!win) throw new Error('El navegador bloqueó la ventana emergente');
  win.document.write(html);
  win.document.close();
  setTimeout(() => { try { win.focus(); win.print(); } catch { /* sin impresora */ } }, 300);
}
